import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
} from 'react'
import * as Icon from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import useAxios from 'axios-hooks'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { Spinner, UncontrolledTooltip } from 'reactstrap'
import DataTable from 'react-data-table-component'
import { toast } from 'react-toastify'

import useSelectedProject from '~/hooks/useSelectedProject'
import Breadcrumb from '~/components/Breadcrumb'
import { Show } from '~/components/commons'
import {
  Body,
  KeywordsHeader,
  SubHeader,
  KeywordsContainer,
  ContentWrapper,
  ButtonTemplate,
  Loading,
  SessionHeroContainer,
} from './styles'
import { NumberParam, StringParam, withDefault } from 'use-query-params'
import SearchInputImperative from '~/components/ecto/SearchInputImperative'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import DatePicker from './components/DatePicker'
import FilterButton from '~/components/FilterButton'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import KeywordsInsightFilterHandler from '~/components/Filters/KeywordsInsight/Handler'

export const PageContext = createContext({})

const customStyles = {
  tableWrapper: {
    style: {
      border: '1px solid #EEECF3',
      borderBottom: '0',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#F3F2F7',
      color: '#5E5873',
      fontWeight: 600,
      paddingLeft: '80px',
    },
  },
  cells: {
    style: {
      paddingLeft: '80px',
    },
  },
}

const Tooltip = ({ index, field, text }) => {
  return (
    <UncontrolledTooltip placement="top" target={`${field}-${index}`}>
      {text}
    </UncontrolledTooltip>
  )
}

const columns = [
  {
    name: <FormattedMessage id="Keyword" />,
    maxWidth: '50%',
    cell: (row, index) => (
      <>
        <p id={`name-${index}`} className="text-bold-500 text-truncate mb-0">
          {row.name}
        </p>
        <Tooltip index={index} field="name" text={row.name} />
      </>
    ),
  },
  {
    name: <FormattedMessage id="Searches" />,
    maxWidth: '50%',
    cell: (row, index) => (
      <>
        <p
          id={`searches-${index}`}
          className="text-bold-500 text-truncate mb-0"
        >
          {row.searches}
        </p>
        <Tooltip index={index} field="searches" text={row.searches} />
      </>
    ),
  },
]

const LoadingData = () => {
  return (
    <Loading>
      <Spinner color="primary" size="lg" />
    </Loading>
  )
}

const InsightsKeywords = () => {
  const intl = useIntl()
  const accountId = useSelector(state => state?.user?.profile?.account?.id)
  const projectId = useSelectedProject(project => project?.id)
  const [fetchingKeywords, setFetchingKeywords] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [tableData, setTableData] = useState(null)

  const [filterOn, setFilterOn] = React.useState(false)
  const { query, setQuery, setQueries } = useSearchParamsHandler()

  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
    status: query.status,
  })

  const [{ data, loading: fetching }, fetchKeywords] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/keywords`,
      params: {
        ...query,
        period:
          projectId === '7f77bb82-2e71-4848-b13b-be245cab9cda'
            ? '2024-07'
            : query.period,
      },
    },
    { useCache: false, manual: true }
  )

  const [, downloadKeywordsCSV] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/keywords/csv`,
      params: query,
    },
    { useCache: false, manual: true }
  )

  const fetchingRef = useRef(null)
  const batchGetBodyRev = useRef({})
  const inputSearchRef = useRef()

  fetchingRef.current = fetching

  const handleFetchKeywords = useCallback(
    _.debounce(params => {
      fetchKeywords({
        data: {
          ...batchGetBodyRev.current,
          ...params,
        },
      }).finally(_ => {
        setFetchingKeywords(false)
      })
    }, 250),
    [fetchKeywords]
  )

  const setSearchParams = _.debounce(search => {
    setFetchingKeywords(true)
    setQueries({ page: 1, search })
  }, 500)

  const setLimitParams = limit => {
    setFetchingKeywords(true)
    setQueries({ page: 1, limit })
  }

  const setPageParams = page => {
    setFetchingKeywords(true)
    setQuery('page', page)
  }

  const fillSearchInput = useCallback(() => {
    inputSearchRef.current.setValue(query.search || '')
  }, [query])

  const paginationComponentOptions = {
    rowsPerPageText: intl.formatMessage({ id: 'table.pagination' }),
    rangeSeparatorText: intl.formatMessage({ id: 'table.rangeSeparator' }),
    selectAllRowsItem: false,
  }

  const downloadCSV = async () => {
    setDownloading(true)
    const { page, limit, search, period } = query
    downloadKeywordsCSV({
      data: {
        ...batchGetBodyRev.current,
        page,
        limit,
        search,
        period,
      },
    })
      .then(({ data }) => {
        const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8,' })
        const blobURL = window.URL.createObjectURL(blob)
        // Create new tag for download file
        const anchor = document.createElement('a')
        anchor.download = 'keywords-insights.csv'
        anchor.href = blobURL
        anchor.dataset.downloadurl = [
          'text/csv',
          anchor.download,
          anchor.href,
        ].join(':')
        anchor.click()
        // Remove URL.createObjectURL. The browser should not save the reference to the file.
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobURL)
        }, 100)
      })
      .catch(e => {
        toast.error('Error downloading your CSV file.')
        console.error(e)
      })
      .finally(() => setDownloading(false))
  }

  useEffect(() => {
    const { page, limit, search, period } = query

    handleFetchKeywords({ period, page, limit, search })
  }, [query, accountId, handleFetchKeywords])

  useEffect(() => {
    fillSearchInput()
  }, [fillSearchInput])

  useEffect(() => {
    if (!data) return

    const processedKeywords = data?.keywords?.map(keyword => ({
      ...keyword,
      searches:
        keyword.type === 'threshold' && keyword.countid === '1'
          ? `<${intl.formatNumber(keyword.searches)}`
          : keyword.type === 'threshold'
          ? `~${intl.formatNumber(keyword.searches)}`
          : intl.formatNumber(keyword.searches),
    }))
    setTableData({ ...data, keywords: processedKeywords })
  }, [data, intl])
  return (
    <>
      <KeywordsHeader>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link to="/">
                  <Icon.Home size={20} />
                </Link>
              ),
            },
            { text: 'Insights' },
            { text: 'Keywords Report' },
          ]}
        />

        <ButtonTemplate
          className="ml-2 border-0"
          onClick={() => downloadCSV()}
          disabled={downloading}
        >
          <Icon.Download height={14} className="d-none d-md-block" />
          <FormattedMessage id="CSV" />
        </ButtonTemplate>
      </KeywordsHeader>
      <SessionHeroContainer>
        <div className="wrapper">
          <h2>
            <FormattedMessage id="Keywords" />
          </h2>
          <div className="buttons">
            <DatePicker />
            <FilterButton
              isActive={filterOn}
              filterCount={filterCount}
              onClick={() => setFilterOn(prevState => !prevState)}
            />
            <KeywordsInsightFilterHandler
              open={filterOn}
              onClose={() => setFilterOn(false)}
            />
          </div>
        </div>
      </SessionHeroContainer>
      <KeywordsContainer>
        <ContentWrapper>
          <Body>
            <SubHeader>
              <strong>
                <Show condition={!_.isEmpty(data) && !fetchingKeywords}>
                  <FormattedMessage
                    id="Keywords and Searches"
                    values={{
                      keywordsTotal: intl.formatNumber(
                        data ? data.keywordsTotal : 0
                      ),
                      searchesTotal: intl.formatNumber(
                        data ? data.searchesTotal : 0
                      ),
                    }}
                  />
                </Show>
                <Show condition={fetchingKeywords}>
                  <FormattedMessage id="Fetching Keywords..." />
                </Show>
              </strong>
            </SubHeader>

            <SearchInputImperative
              placeholder="Search"
              onChange={setSearchParams}
              disabled={fetchingKeywords || tableData?.keywordsTotal === 0}
              ref={inputSearchRef}
            />

            <div className="d-flex flex-column mt-2">
              <DataTable
                actions
                keyField="keyword"
                className="dataTable-custom"
                columns={columns}
                data={tableData?.keywords}
                noHeader
                pagination
                paginationServer
                paginationTotalRows={tableData?.keywordsTotal}
                onChangePage={setPageParams}
                onChangeRowsPerPage={setLimitParams}
                progressPending={fetchingKeywords}
                progressComponent={<LoadingData />}
                customStyles={customStyles}
                paginationComponentOptions={paginationComponentOptions}
              />
            </div>
          </Body>
        </ContentWrapper>
      </KeywordsContainer>
    </>
  )
}

const Wrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )
  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
        status: withDefault(arrayParam, []),
        page: NumberParam,
        limit: NumberParam,
        search: StringParam,
        period: StringParam,
      }}
    >
      <InsightsKeywords />
    </SearchParamsHandlerProvider>
  )
}

InsightsKeywords.displayName = 'Insights Keywords Page'

export default Wrapper
